// Compartilhar localização (pedido 2026-09-23) — aberto pelo botão verde de
// compartilhar no mapa do PWA (mobile-app.tsx). O cliente escolhe veículo,
// validade do link e o que o convidado pode ver/fazer; o link abre a tela
// pública /ao-vivo/:token (routes/ao-vivo.$token.tsx). Overlay próprio em
// tela cheia (não Dialog do shadcn) — mesmo padrão dos outros modais do PWA,
// e evita o bug de z-index de Select/Popover dentro de Dialog.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Copy,
  Eye,
  Gauge,
  Loader2,
  MapPin,
  Navigation,
  Route as RouteIcon,
  Share2,
  Sparkles,
  Car,
  User,
  X,
  Link2Off,
  Lock,
} from "lucide-react";
import { usePermission } from "@/hooks/use-permission";
import { cn } from "@/lib/utils";
import { createPortal } from "react-dom";
import type { Vehicle } from "@/types";
import {
  formatRemaining,
  gpsDriveLocationShareService,
  locationShareUrl,
  type LocationShare,
  type LocationSharePermissions,
} from "@/services/gpsdrive-location-share.service";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { LicensePlate } from "@/components/license-plate";

const DURATIONS: { minutes: number; label: string }[] = [
  { minutes: 15, label: "15 min" },
  { minutes: 30, label: "30 min" },
  { minutes: 60, label: "1 hora" },
  { minutes: 120, label: "2 horas" },
  { minutes: 240, label: "4 horas" },
  { minutes: 480, label: "8 horas" },
  { minutes: 1440, label: "24 horas" },
  { minutes: 4320, label: "3 dias" },
  { minutes: 10080, label: "7 dias" },
];

const PERMISSION_ITEMS: {
  key: keyof LocationSharePermissions;
  label: string;
  hint: string;
  icon: typeof Gauge;
}[] = [
  { key: "showSpeed", label: "Velocidade", hint: "Mostra a quantos km/h o veículo está", icon: Gauge },
  { key: "showAddress", label: "Endereço", hint: "Nome da rua e bairro onde está agora", icon: MapPin },
  { key: "showVehicleDetails", label: "Foto, placa e modelo", hint: "Identifica qual é o veículo", icon: Car },
  { key: "allowDirections", label: "Traçar rota até o veículo", hint: "Navegação até o veículo dentro do link (recalcula sozinha)", icon: Navigation },
  { key: "showSharerName", label: "Mostrar meu nome", hint: "Aparece quem compartilhou", icon: User },
  // Nunca entra no "Tudo liberado" — bloquear é ação, não visualização.
  { key: "allowBlock", label: "Bloquear e desbloquear", hint: "A pessoa pode bloquear/desbloquear o veículo pelo link (fica registrado)", icon: Lock },
];

const ONLY_VIEW: LocationSharePermissions = {
  showSpeed: false,
  showAddress: false,
  showTrail: false,
  showVehicleDetails: false,
  allowDirections: false,
  showSharerName: true,
  allowBlock: false,
};
const EVERYTHING: LocationSharePermissions = {
  showSpeed: true,
  showAddress: true,
  showTrail: true,
  showVehicleDetails: true,
  allowDirections: true,
  showSharerName: true,
  allowBlock: false,
};

const SHARES_KEY = ["location-shares", "mine"] as const;

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // WebView antiga/sem permissão de clipboard — fallback clássico.
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function shareText(url: string, vehicleName: string | null, minutesLabel: string | null): string {
  const what = vehicleName ? `a localização ao vivo do ${vehicleName}` : "minha localização ao vivo";
  return `📍 Acompanhe ${what}${minutesLabel ? ` (link válido por ${minutesLabel})` : ""}: ${url}`;
}

async function nativeShare(url: string, text: string): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.share) return false;
  try {
    await navigator.share({ title: "Localização ao vivo", text, url });
    return true;
  } catch {
    // Usuário cancelou a folha de compartilhar — não é erro.
    return true;
  }
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function LocationShareSheet({
  vehicles,
  initialVehicleId,
  onClose,
  asDialog = false,
}: {
  vehicles: Vehicle[];
  initialVehicleId: string | null;
  onClose: () => void;
  asDialog?: boolean;
}) {
  const queryClient = useQueryClient();
  const canLock = usePermission("devices", "lock");
  const canUnlock = usePermission("devices", "unlock");
  const permissionItems = PERMISSION_ITEMS.filter((i) => i.key !== "allowBlock" || canLock || canUnlock);
  const [tab, setTab] = useState<"new" | "active">("new");
  const [vehicleId, setVehicleId] = useState<string | null>(initialVehicleId ?? vehicles[0]?.id ?? null);
  const [duration, setDuration] = useState(60);
  const [perms, setPerms] = useState<LocationSharePermissions>(EVERYTHING);
  const [guestName, setGuestName] = useState("");
  const [message, setMessage] = useState("");
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [created, setCreated] = useState<LocationShare | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sharesQuery = useQuery({
    queryKey: SHARES_KEY,
    queryFn: () => gpsDriveLocationShareService.listMine(),
    staleTime: 15_000,
  });
  const activeShares = (sharesQuery.data ?? []).filter((s) => s.active);

  const selectedVehicle = vehicles.find((v) => v.id === vehicleId) ?? null;
  const filteredVehicles = useMemo(() => {
    const q = vehicleQuery.trim().toLowerCase();
    const list = q
      ? vehicles.filter((v) => v.name.toLowerCase().includes(q) || v.plate?.toLowerCase().includes(q))
      : vehicles;
    return list.slice(0, 30);
  }, [vehicles, vehicleQuery]);

  const preset: "view" | "all" | "custom" =
    JSON.stringify(perms) === JSON.stringify(ONLY_VIEW)
      ? "view"
      : JSON.stringify(perms) === JSON.stringify(EVERYTHING)
        ? "all"
        : "custom";

  const createMutation = useMutation({
    mutationFn: () =>
      gpsDriveLocationShareService.create({
        traccarDeviceId: Number(vehicleId),
        durationMinutes: duration,
        ...perms,
        guestName: guestName.trim() || undefined,
        message: message.trim() || undefined,
      }),
    onSuccess: (share) => {
      setError(null);
      setCreated(share);
      queryClient.invalidateQueries({ queryKey: SHARES_KEY });
    },
    onError: (err) => {
      setError(
        err instanceof GpsDriveApiError && err.status === 403
          ? "Você não tem acesso a este veículo."
          : "Não foi possível gerar o link. Tente de novo.",
      );
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => gpsDriveLocationShareService.revoke(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SHARES_KEY }),
  });

  const durationLabel = DURATIONS.find((d) => d.minutes === duration)?.label ?? null;

  const sheet = (
    <div className={cn("flex flex-col bg-slate-50 dark:bg-slate-950", asDialog ? "absolute inset-0" : "fixed inset-0 z-50")}>
      <style>{`
        @keyframes lsPop { 0% { transform: scale(.6); opacity: 0 } 70% { transform: scale(1.08) } 100% { transform: scale(1); opacity: 1 } }
        @keyframes lsFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-6px) } }
      `}</style>

      {/* Header */}
      <div
        className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-500 to-sky-500 px-4 pb-5 text-white"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-white/20">
              <Share2 className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-bold leading-tight">Compartilhar localização</h2>
              <p className="text-xs text-white/85">Link ao vivo, com prazo pra acabar</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/20"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-1 rounded-xl bg-white/15 p-1 text-sm font-semibold">
          <button
            type="button"
            onClick={() => setTab("new")}
            className={cn("rounded-lg py-2", tab === "new" ? "bg-white text-teal-700" : "text-white")}
          >
            Novo link
          </button>
          <button
            type="button"
            onClick={() => setTab("active")}
            className={cn("rounded-lg py-2", tab === "active" ? "bg-white text-teal-700" : "text-white")}
          >
            Links ativos{activeShares.length > 0 ? ` (${activeShares.length})` : ""}
          </button>
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto px-4 pt-4"
        style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
      >
        {tab === "active" ? (
          <ActiveSharesList
            shares={activeShares}
            loading={sharesQuery.isLoading}
            vehicles={vehicles}
            onRevoke={(id) => revokeMutation.mutate(id)}
            revokingId={revokeMutation.isPending ? revokeMutation.variables : null}
            onNew={() => setTab("new")}
          />
        ) : created ? (
          <CreatedResult
            share={created}
            vehicleName={created.showVehicleDetails ? (selectedVehicle?.name ?? null) : null}
            durationLabel={durationLabel}
            onAnother={() => setCreated(null)}
            onDone={onClose}
          />
        ) : (
          <div className="space-y-5">
            {/* Veículo */}
            <Section title="Qual veículo?" emoji="🚗">
              {vehicles.length > 6 && (
                <input
                  value={vehicleQuery}
                  onChange={(e) => setVehicleQuery(e.target.value)}
                  placeholder="Buscar por nome ou placa"
                  className="mb-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-teal-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  style={{ fontSize: 16 }}
                />
              )}
              <div className="flex gap-2 overflow-x-auto pb-1">
                {filteredVehicles.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setVehicleId(v.id)}
                    className={cn(
                      "shrink-0 rounded-xl border px-3 py-2 text-left text-sm transition",
                      v.id === vehicleId
                        ? "border-teal-500 bg-teal-50 text-teal-800 ring-2 ring-teal-500/30 dark:bg-teal-950/40 dark:text-teal-200"
                        : "border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200",
                    )}
                  >
                    <div className="max-w-[10rem] truncate font-semibold">{v.name}</div>
                    {v.plate && <div className="mt-0.5"><LicensePlate plate={v.plate} size="xs" /></div>}
                  </button>
                ))}
                {filteredVehicles.length === 0 && (
                  <p className="text-sm text-slate-500">Nenhum veículo encontrado.</p>
                )}
              </div>
            </Section>

            {/* Validade */}
            <Section title="Por quanto tempo o link vale?" emoji="⏳">
              <div className="grid grid-cols-3 gap-2">
                {DURATIONS.map((d) => (
                  <button
                    key={d.minutes}
                    type="button"
                    onClick={() => setDuration(d.minutes)}
                    className={cn(
                      "rounded-xl border py-2.5 text-sm font-semibold transition",
                      duration === d.minutes
                        ? "border-teal-500 bg-teal-500 text-white shadow-md shadow-teal-500/30"
                        : "border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200",
                    )}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Depois disso o link para de funcionar sozinho. Você também pode desativar antes, em "Links ativos".
              </p>
            </Section>

            {/* Permissões */}
            <Section title="O que a pessoa pode ver?" emoji="👀">
              <div className="mb-3 grid grid-cols-3 gap-2">
                <PresetButton active={preset === "view"} onClick={() => setPerms(ONLY_VIEW)} title="Só o ponto" sub="no mapa" />
                <PresetButton active={preset === "all"} onClick={() => setPerms(EVERYTHING)} title="Tudo" sub="liberado" />
                <PresetButton active={preset === "custom"} onClick={() => undefined} title="Personalizado" sub="escolha abaixo" />
              </div>
              <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-700 dark:bg-slate-900">
                {permissionItems.map((item) => {
                  const Icon = item.icon;
                  const on = perms[item.key];
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setPerms((p) => ({ ...p, [item.key]: !p[item.key] }))}
                      className="flex w-full items-center gap-3 px-3 py-3 text-left"
                    >
                      <span
                        className={cn(
                          "grid h-9 w-9 shrink-0 place-items-center rounded-full",
                          on ? "bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300" : "bg-slate-100 text-slate-400 dark:bg-slate-800",
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">{item.label}</span>
                        <span className="block text-xs text-slate-500 dark:text-slate-400">{item.hint}</span>
                      </span>
                      <span
                        className={cn(
                          "relative h-6 w-11 shrink-0 rounded-full transition",
                          on ? "bg-teal-500" : "bg-slate-300 dark:bg-slate-700",
                        )}
                      >
                        <span
                          className={cn(
                            "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all",
                            on ? "left-[22px]" : "left-0.5",
                          )}
                        />
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                <Eye className="h-3.5 w-3.5" /> O convidado só visualiza — nunca envia comandos nem vê outros veículos.
              </p>
            </Section>

            {/* Extras */}
            <Section title="Um toque especial (opcional)" emoji="✨">
              <input
                value={guestName}
                onChange={(e) => setGuestName(e.target.value.slice(0, 60))}
                placeholder="Pra quem é? Ex.: Mãe, João"
                className="mb-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-teal-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                style={{ fontSize: 16 }}
              />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 200))}
                placeholder="Mensagem pro convidado. Ex.: Tô chegando! 🚀"
                rows={2}
                className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-teal-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                style={{ fontSize: 16 }}
              />
            </Section>

            {error && (
              <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
                {error}
              </p>
            )}

            <button
              type="button"
              disabled={!vehicleId || createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500 py-4 text-base font-bold text-white shadow-lg shadow-teal-500/30 disabled:opacity-60"
            >
              {createMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
              Gerar link mágico
            </button>
          </div>
        )}
      </div>
    </div>
  );
  // Desktop (detalhes do veículo no mapa, 2026-09-29): mesma tela, num
  // modal centralizado em vez de ocupar a janela inteira.
  // Portal: o painel de detalhes pode estar dentro de um container com
  // transform, que prenderia o "fixed" nele.
  return asDialog ? createPortal(
    <div className="fixed inset-0 z-[1200] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="relative h-[min(780px,92vh)] w-full max-w-md overflow-hidden rounded-2xl shadow-2xl [transform:translateZ(0)]" onClick={(e) => e.stopPropagation()}>
        {sheet}
      </div>
    </div>,
    document.body,
  ) : (
    sheet
  );
}

function Section({ title, emoji, children }: { title: string; emoji: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
        <span className="text-base">{emoji}</span>
        {title}
      </h3>
      {children}
    </section>
  );
}

function PresetButton({
  active,
  onClick,
  title,
  sub,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  sub: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-xl border px-2 py-2 text-center transition",
        active
          ? "border-teal-500 bg-teal-50 text-teal-800 dark:bg-teal-950/40 dark:text-teal-200"
          : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300",
      )}
    >
      <div className="text-sm font-bold">{title}</div>
      <div className="text-[11px] opacity-70">{sub}</div>
    </button>
  );
}

function CreatedResult({
  share,
  vehicleName,
  durationLabel,
  onAnother,
  onDone,
}: {
  share: LocationShare;
  vehicleName: string | null;
  durationLabel: string | null;
  onAnother: () => void;
  onDone: () => void;
}) {
  const url = locationShareUrl(share.token);
  const text = shareText(url, vehicleName, durationLabel);
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div className="flex flex-col items-center pt-4 text-center">
      <div className="text-6xl" style={{ animation: "lsPop .5s ease-out both, lsFloat 2.4s ease-in-out .5s infinite" }}>
        🎉
      </div>
      <h3 className="mt-3 text-xl font-extrabold text-slate-800 dark:text-slate-100">Link pronto!</h3>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Válido por <b>{durationLabel ?? formatRemaining(new Date(share.expiresAt).getTime() - Date.now())}</b>
        {share.guestName ? (
          <>
            {" "}
            · pra <b>{share.guestName}</b>
          </>
        ) : null}
      </p>

      <div className="mt-5 w-full break-all rounded-2xl border border-dashed border-teal-400 bg-teal-50 px-3 py-3 text-left text-sm text-teal-900 dark:bg-teal-950/30 dark:text-teal-200">
        {url}
      </div>

      <div className="mt-4 grid w-full gap-2">
        {canNativeShare && (
          <button
            type="button"
            onClick={() => void nativeShare(url, text)}
            className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500 py-3.5 font-bold text-white shadow-lg shadow-teal-500/30"
          >
            <Share2 className="h-5 w-5" /> Compartilhar
          </button>
        )}
        <a
          href={`https://wa.me/?text=${encodeURIComponent(text)}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] py-3.5 font-bold text-white shadow-lg shadow-green-500/30"
        >
          <span className="text-lg">💬</span> Enviar no WhatsApp
        </a>
        <button
          type="button"
          onClick={async () => {
            if (await copyText(url)) {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }
          }}
          className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3.5 font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        >
          {copied ? <Check className="h-5 w-5 text-emerald-500" /> : <Copy className="h-5 w-5" />}
          {copied ? "Copiado!" : "Copiar link"}
        </button>
      </div>

      <div className="mt-6 flex w-full gap-2">
        <button
          type="button"
          onClick={onAnother}
          className="flex-1 rounded-xl py-3 text-sm font-semibold text-teal-700 dark:text-teal-300"
        >
          Criar outro
        </button>
        <button
          type="button"
          onClick={onDone}
          className="flex-1 rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white dark:bg-slate-800"
        >
          Concluir
        </button>
      </div>
    </div>
  );
}

function ActiveSharesList({
  shares,
  loading,
  vehicles,
  onRevoke,
  revokingId,
  onNew,
}: {
  shares: LocationShare[];
  loading: boolean;
  vehicles: Vehicle[];
  onRevoke: (id: string) => void;
  revokingId: string | null | undefined;
  onNew: () => void;
}) {
  const now = useNow(30_000);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex justify-center py-12 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (shares.length === 0) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <div className="text-5xl" style={{ animation: "lsFloat 2.4s ease-in-out infinite" }}>
          🛰️
        </div>
        <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">Nenhum link ativo</p>
        <p className="mt-1 text-sm text-slate-500">Ninguém está vendo sua localização agora.</p>
        <button
          type="button"
          onClick={onNew}
          className="mt-5 rounded-xl bg-teal-500 px-5 py-2.5 text-sm font-bold text-white"
        >
          Criar um link
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {shares.map((s) => {
        const vehicle = vehicles.find((v) => Number(v.id) === s.traccarDeviceId);
        const remainingMs = new Date(s.expiresAt).getTime() - now;
        const url = locationShareUrl(s.token);
        return (
          <div
            key={s.id}
            className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-800 dark:text-slate-100">
                  {vehicle?.name ?? `Veículo #${s.traccarDeviceId}`}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {s.guestName ? `Pra ${s.guestName} · ` : ""}
                  {s.viewCount === 0 ? "ainda não aberto" : `aberto ${s.viewCount}x`}
                </div>
              </div>
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                {formatRemaining(remainingMs)}
              </span>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  if (await copyText(url)) {
                    setCopiedId(s.id);
                    setTimeout(() => setCopiedId(null), 2000);
                  }
                }}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-slate-100 py-2 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {copiedId === s.id ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                {copiedId === s.id ? "Copiado!" : "Copiar"}
              </button>
              <button
                type="button"
                disabled={revokingId === s.id}
                onClick={() => onRevoke(s.id)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-rose-50 py-2 text-sm font-semibold text-rose-600 disabled:opacity-60 dark:bg-rose-950/40 dark:text-rose-300"
              >
                {revokingId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2Off className="h-4 w-4" />}
                Desativar
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
